import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Plus, Trash2, Loader2, FileDown, ArrowLeft, ChevronRight, Sparkles,
  Star, BarChart3,
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { API } from "../App";

/**
 * MhM Self-Serve Project Compare Page.
 *
 * Lets the buyer add homes they're considering, rate each on the priorities
 * that matter to them, and run a fit comparison — same capability that existed
 * in MhM v1 but dropped during the v2 rebuild. The backend endpoints
 * (`POST /projects`, `PUT /projects/{id}/scores`, `POST /compare`) never went
 * away.
 *
 * Route: `/mhm/projects/:sessionId`
 */

const fKey = (f) => f.key || f.factor_id;
const fLabel = (f) => f.buyerLabelV3 || f.buyerLabelV2 || f.buyerLabel || f.label || f.name;

const MhMProjectComparePage = () => {
  const { sessionId } = useParams();

  const [session, setSession] = useState(null);
  const [factors, setFactors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Add-project form
  const [newName, setNewName] = useState("");
  const [newLoc, setNewLoc] = useState("");
  const [adding, setAdding] = useState(false);

  // Scoring modal state
  const [scoringPid, setScoringPid] = useState(null);
  const [scores, setScores] = useState({});

  // Comparison state
  const [comparison, setComparison] = useState(null);
  const [comparing, setComparing] = useState(false);

  // Boot
  useEffect(() => {
    (async () => {
      try {
        const [sessRes, cfgRes] = await Promise.all([
          axios.get(`${API}/mhm/sessions/${sessionId}`),
          axios.get(`${API}/mhm/config`),
        ]);
        setSession(sessRes.data);
        setFactors(cfgRes.data.factors || []);
        setProjects(sessRes.data.projects || []);
      } catch (e) {
        setError(e.response?.status === 404 ? "Session not found" : "Could not load your MhM session");
      } finally { setLoading(false); }
    })();
  }, [sessionId]);

  const fingerprint = session?.fingerprint;
  const activeFactors = factors.filter((f) => (fingerprint?.weights?.[fKey(f)] || 0) > 0);
  const allScored = projects.length >= 2 && projects.every((p) => Object.keys(p.scores || {}).length > 0);
  const hasScoredProjects = projects.some((p) => p.fit_score != null);

  const bandFor = (fk) => {
    if (session?.high_priorities?.includes(fk)) return { label: "Top Priority", tone: "bg-[#F0F5EC] text-[#5A7A4A]" };
    if (session?.medium_priorities?.includes(fk)) return { label: "Important", tone: "bg-[#FBF3EE] text-[#8C5A38]" };
    if (session?.low_priorities?.includes(fk)) return { label: "Flexible", tone: "bg-[#EAF0F0] text-[#3D6A6A]" };
    return null;
  };

  const addProject = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    try {
      const r = await axios.post(`${API}/mhm/sessions/${sessionId}/projects`, {
        name: newName.trim(),
        location: newLoc.trim(),
      });
      setProjects([...projects, {
        project_id: r.data.project_id,
        name: newName.trim(),
        location: newLoc.trim(),
        scores: {},
        fit_score: null,
      }]);
      setNewName("");
      setNewLoc("");
    } catch {
      toast.error("Could not add project");
    } finally { setAdding(false); }
  };

  const removeProject = async (pid) => {
    try {
      await axios.delete(`${API}/mhm/sessions/${sessionId}/projects/${pid}`);
      setProjects(projects.filter((p) => p.project_id !== pid));
      if (comparison) setComparison(null);
    } catch {
      toast.error("Could not remove project");
    }
  };

  const openScoring = (pid) => {
    const proj = projects.find((p) => p.project_id === pid);
    setScoringPid(pid);
    // Pre-populate every active factor with a default 5 so a save without
    // touching sliders still produces a complete rating.
    const seed = { ...(proj?.scores || {}) };
    activeFactors.forEach((f) => {
      const k = fKey(f);
      if (seed[k] == null) seed[k] = 5;
    });
    setScores(seed);
  };

  const saveScores = async () => {
    try {
      await axios.put(`${API}/mhm/sessions/${sessionId}/projects/${scoringPid}/scores`, { scores });
      setProjects(projects.map((p) => p.project_id === scoringPid ? { ...p, scores } : p));
      setScoringPid(null);
      toast.success("Ratings saved");
    } catch {
      toast.error("Could not save ratings");
    }
  };

  const runCompare = async () => {
    setComparing(true);
    try {
      const r = await axios.post(`${API}/mhm/sessions/${sessionId}/compare`);
      setComparison(r.data);
      setProjects(r.data.projects);
      toast.success("Comparison ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not compare projects");
    } finally { setComparing(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center" data-testid="mhm-projects-loading">
        <div className="text-center">
          <Loader2 className="w-7 h-7 animate-spin text-[#87A96B] mx-auto" />
          <p className="text-sm text-[#6D6660] mt-3">Loading your priorities...</p>
        </div>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <h2 className="text-lg font-bold text-[#2C3E50] mb-2">Session not available</h2>
          <p className="text-sm text-[#6D6660] mb-5">{error || "We couldn't load this MhM session."}</p>
          <a href="/mhm/start" className="inline-flex items-center gap-2 bg-[#87A96B] hover:bg-[#76955d] text-white px-5 py-2.5 rounded-full text-sm font-medium">
            Start a fresh MhM
          </a>
        </div>
      </div>
    );
  }

  if (!fingerprint) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <h2 className="text-lg font-bold text-[#2C3E50] mb-2">Finish your priorities first</h2>
          <p className="text-sm text-[#6D6660] mb-5">Complete the MhM assessment so we know what matters most to you — then you can compare homes against it.</p>
          <a href="/mhm/start" className="inline-flex items-center gap-2 bg-[#87A96B] hover:bg-[#76955d] text-white px-5 py-2.5 rounded-full text-sm font-medium">
            Take MhM Assessment
          </a>
        </div>
      </div>
    );
  }

  // ===== Scoring screen =====
  if (scoringPid) {
    const proj = projects.find((p) => p.project_id === scoringPid);
    return (
      <div className="min-h-screen bg-[#FAF8F5]" data-testid="mhm-scoring-screen">
        <Header subtitle="Rate this home on what matters to you" />
        <div className="max-w-2xl mx-auto px-4 py-6">
          <Button variant="ghost" onClick={() => setScoringPid(null)} className="text-[#6D6660] mb-3" data-testid="mhm-scoring-back">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h2 className="text-xl font-bold text-[#2C3E50] mb-1 font-['Manrope']">Rate: {proj?.name}</h2>
          <p className="text-xs text-[#8B8580] mb-4">How well does this home perform on each priority? Slide 0 (poor) to 10 (excellent), based on what you know so far.</p>

          <div className="space-y-2.5">
            {activeFactors.map((f) => {
              const key = fKey(f);
              const band = bandFor(key);
              const val = scores[key] ?? 5;
              return (
                <div key={key} className="bg-white border border-[#E8E3DE] rounded-xl p-3" data-testid={`mhm-score-row-${key}`}>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="text-sm font-medium text-[#2C3E50]">{fLabel(f)}</p>
                    {band && <Badge className={`text-[10px] ${band.tone} border-0`}>{band.label}</Badge>}
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min="0"
                      max="10"
                      value={val}
                      onChange={(e) => setScores({ ...scores, [key]: parseInt(e.target.value, 10) })}
                      className="flex-1 h-1.5 accent-[#87A96B]"
                      data-testid={`mhm-score-slider-${key}`}
                    />
                    <span className="text-base font-bold text-[#87A96B] w-7 text-center">{val}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex gap-2 mt-5 sticky bottom-2">
            <Button variant="outline" className="flex-1 rounded-full" onClick={() => setScoringPid(null)} data-testid="mhm-scoring-cancel">Cancel</Button>
            <Button className="flex-1 bg-[#87A96B] hover:bg-[#76955d] text-white rounded-full" onClick={saveScores} data-testid="mhm-scoring-save">Save Ratings</Button>
          </div>
        </div>
      </div>
    );
  }

  // ===== List + add screen =====
  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="mhm-projects-page">
      <Header subtitle="Add the homes you're considering and rate them against your priorities" />
      <div className="max-w-2xl mx-auto px-4 py-6">

        {/* Add-project card */}
        <Card className="border-[#E8E3DE] mb-4">
          <CardContent className="p-4 space-y-2">
            <p className="text-xs uppercase tracking-wide text-[#8B8580] font-semibold">Add a home</p>
            <Input
              placeholder="Project / home name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              data-testid="mhm-project-name-input"
            />
            <div className="flex gap-2">
              <Input
                placeholder="Location (optional)"
                value={newLoc}
                onChange={(e) => setNewLoc(e.target.value)}
                className="flex-1"
                data-testid="mhm-project-location-input"
              />
              <Button
                onClick={addProject}
                disabled={!newName.trim() || adding}
                className="bg-[#87A96B] hover:bg-[#76955d] text-white rounded-full px-4"
                data-testid="mhm-project-add-btn"
              >
                {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Add</>}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Project list */}
        {projects.length === 0 ? (
          <div className="text-center py-10 bg-white rounded-2xl border border-[#E8E3DE]">
            <Sparkles className="w-7 h-7 text-[#87A96B] mx-auto mb-2" />
            <p className="text-sm text-[#6D6660]">No homes added yet. Start with the ones you've shortlisted.</p>
          </div>
        ) : (
          <div className="space-y-2 mb-4" data-testid="mhm-projects-list">
            {projects.map((p) => {
              const scoreCount = Object.keys(p.scores || {}).length;
              const scored = scoreCount > 0;
              return (
                <div
                  key={p.project_id}
                  className="flex items-center gap-2 p-3 rounded-xl border border-[#E8E3DE] bg-white"
                  data-testid={`mhm-project-row-${p.project_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#2C3E50] truncate">{p.name}</p>
                    {p.location && <p className="text-[11px] text-[#8B8580] truncate">{p.location}</p>}
                  </div>
                  {p.fit_score != null && (
                    <span className="text-sm font-bold text-[#87A96B] px-2">{p.fit_score}%</span>
                  )}
                  <Badge variant={scored ? "default" : "outline"} className={scored ? "bg-[#F0F5EC] text-[#5A7A4A] border-0" : ""}>
                    {scored ? "Rated" : "Pending"}
                  </Badge>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openScoring(p.project_id)}
                    data-testid={`mhm-project-rate-${p.project_id}`}
                  >
                    {scored ? "Edit" : "Rate"}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeProject(p.project_id)}
                    data-testid={`mhm-project-remove-${p.project_id}`}
                  >
                    <Trash2 className="w-4 h-4 text-[#B0A898]" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        {projects.length >= 1 && !allScored && (
          <p className="text-[11px] text-[#8B8580] text-center mb-3">
            {projects.length === 1
              ? "Add at least one more home to compare."
              : "Rate every home on your priorities to unlock the comparison."}
          </p>
        )}

        {allScored && (
          <Button
            className="w-full bg-[#87A96B] hover:bg-[#76955d] text-white rounded-full"
            onClick={runCompare}
            disabled={comparing}
            data-testid="mhm-compare-btn"
          >
            {comparing ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Comparing...</> : <>Compare My Homes <ChevronRight className="w-4 h-4 ml-1" /></>}
          </Button>
        )}

        {/* Inline comparison preview */}
        {(comparison || hasScoredProjects) && (
          <div className="mt-6 space-y-2.5" data-testid="mhm-comparison-preview">
            <p className="text-xs uppercase tracking-wide text-[#8B8580] font-semibold">Fit ranking</p>
            {(comparison?.projects || projects)
              .filter((p) => p.fit_score != null)
              .sort((a, b) => (b.fit_score || 0) - (a.fit_score || 0))
              .map((p, i) => (
                <div
                  key={p.project_id}
                  className={`flex items-center gap-3 p-3 rounded-xl border ${i === 0 ? "border-[#87A96B] bg-[#F0F5EC]" : "border-[#E8E3DE] bg-white"}`}
                  data-testid={`mhm-rank-${i}`}
                >
                  <span className="text-xl font-bold text-[#87A96B] w-7 text-center font-['Manrope']">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#2C3E50] truncate">{p.name}</p>
                    {i === 0 && <Badge className="bg-[#87A96B] text-white border-0 text-[10px] mt-0.5">Best Overall Fit</Badge>}
                  </div>
                  <span className="text-lg font-bold text-[#87A96B]">{p.fit_score}%</span>
                </div>
              ))}

            <a
              href={`/mhm/comparison-report/${sessionId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
              data-testid="mhm-open-full-comparison"
            >
              <Button className="w-full mt-2 bg-[#C4835A] hover:bg-[#a86f49] text-white rounded-full">
                <FileDown className="w-4 h-4 mr-2" /> Open Full Comparison Report
              </Button>
            </a>
          </div>
        )}

        <div className="mt-8 flex justify-center gap-3 text-[12px]">
          <a href={`/mhm/report/${sessionId}`} className="text-[#87A96B] hover:underline" data-testid="mhm-projects-back-to-report">
            Back to my Clarity Report
          </a>
          <span className="text-[#C4B8AC]">·</span>
          <a href={`/mhm/next/${sessionId}`} className="text-[#87A96B] hover:underline" data-testid="mhm-projects-other-options">
            Other options
          </a>
        </div>
      </div>
    </div>
  );
};

const Header = ({ subtitle }) => (
  <div className="bg-white border-b border-[#E8E3DE]">
    <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
      <div className="w-9 h-9 rounded-lg bg-[#87A96B] flex items-center justify-center">
        <Star className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-sm font-semibold text-[#2C3E50]">Compare your Homes</p>
        <p className="text-xs text-[#8B8580]">{subtitle}</p>
      </div>
    </div>
  </div>
);

export default MhMProjectComparePage;
